import { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Pages - Lazy loaded for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TripDetail = lazy(() => import("./pages/TripDetail"));
const RoundDetail = lazy(() => import("./pages/RoundDetail"));
const Scorecard = lazy(() => import("./pages/Scorecard"));
const LiveScoring = lazy(() => import("./pages/LiveScoring"));
const Players = lazy(() => import("./pages/Players"));
const Friends = lazy(() => import("./pages/Friends"));
const Stats = lazy(() => import("./pages/Stats"));
const JoinTrip = lazy(() => import("./pages/JoinTrip"));
const PublicLeaderboard = lazy(() => import("./pages/PublicLeaderboard"));
const TeeSheet = lazy(() => import("./pages/TeeSheet"));
const Flights = lazy(() => import("./pages/Flights"));
const Settings = lazy(() => import("./pages/Settings"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CreateBlogPost = lazy(() => import("./pages/CreateBlogPost"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.baseURL = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Add axios interceptor to include session token from localStorage
axios.interceptors.request.use((config) => {
  const sessionToken = localStorage.getItem("session_token");
  if (sessionToken) {
    config.headers["Authorization"] = `Bearer ${sessionToken}`;
  }
  return config;
});

// Auth Callback Component - Google OAuth redirects directly to /dashboard
// This component is kept for backward compatibility but no longer needed
const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Google OAuth callback redirects to /home instead of /dashboard
    // This is just a fallback
    navigate("/home", { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState({ checked: false, user: null });
  const location = useLocation();
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    // Check for session_token in URL (from OAuth callback)
    const urlParams = new URLSearchParams(location.search);
    const sessionToken = urlParams.get("session_token");

    if (sessionToken) {
      // Store token in localStorage and cookie
      localStorage.setItem("session_token", sessionToken);
      document.cookie = `session_token=${sessionToken}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
      // Remove token from URL
      window.history.replaceState({}, "", location.pathname);
    }

    // If user data was passed from AuthCallback, use it directly
    if (location.state?.user && !hasCheckedAuth.current) {
      setAuthState({ checked: true, user: location.state.user });
      hasCheckedAuth.current = true;
      return;
    }

    // If already authenticated, don't re-check
    if (authState.user && authState.checked) {
      return;
    }

    // Only check auth once on initial mount
    if (hasCheckedAuth.current) {
      return;
    }

    // Check auth status with backend
    const checkAuth = async () => {
      hasCheckedAuth.current = true;
      try {
        const response = await axios.get("/auth/me");
        setAuthState({ checked: true, user: response.data });
      } catch (error) {
        setAuthState({ checked: true, user: null });
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [
    location.state?.user,
    location.search,
    location.pathname,
    navigate,
    authState.user,
    authState.checked,
  ]);

  if (!authState.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!authState.user) {
    return <Navigate to="/" replace />;
  }

  return children({ user: authState.user });
};

// Optional Auth Route - checks for user but doesn't redirect
const OptionalAuthRoute = ({ children }) => {
  const [authState, setAuthState] = useState({ checked: false, user: null });
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for session_token in URL (from OAuth callback)
        const urlParams = new URLSearchParams(location.search);
        const sessionToken = urlParams.get("session_token");

        if (sessionToken) {
          localStorage.setItem("session_token", sessionToken);
          document.cookie = `session_token=${sessionToken}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
          window.history.replaceState({}, "", location.pathname);
        }

        const response = await axios.get("/auth/me");
        setAuthState({ checked: true, user: response.data });
      } catch (error) {
        // User not authenticated, but that's okay - just pass null
        setAuthState({ checked: true, user: null });
      }
    };

    checkAuth();
  }, [location.search, location.pathname]);

  if (!authState.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return children({ user: authState.user });
};

// App Router Component
const AppRouter = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/contact" element={<Contact />} />
        <Route
          path="/live/:viewerCode"
          element={
            <OptionalAuthRoute>
              {({ user }) => <PublicLeaderboard user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/home"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Home user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Dashboard user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/trips/:tripId"
          element={
            <OptionalAuthRoute>
              {({ user }) => <TripDetail user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/rounds/:roundId"
          element={
            <OptionalAuthRoute>
              {({ user }) => <RoundDetail user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/players"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Players user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/friends"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Friends user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Settings user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/trips/:tripId/stats"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Stats user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/join/:inviteCode"
          element={
            <OptionalAuthRoute>
              {({ user }) => <JoinTrip user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/rounds/:roundId/tee-sheet"
          element={
            <OptionalAuthRoute>
              {({ user }) => <TeeSheet user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/rounds/:roundId/scorecard"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Scorecard user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/rounds/:roundId/live"
          element={
            <OptionalAuthRoute>
              {({ user }) => <LiveScoring user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/trips/:tripId/flights"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Flights user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/blog"
          element={
            <OptionalAuthRoute>
              {({ user }) => <Blog user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/blog/post/:postId"
          element={
            <OptionalAuthRoute>
              {({ user }) => <BlogPost user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/blog/create"
          element={
            <OptionalAuthRoute>
              {({ user }) => <CreateBlogPost user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route
          path="/blog/edit/:postId"
          element={
            <OptionalAuthRoute>
              {({ user }) => <CreateBlogPost user={user} />}
            </OptionalAuthRoute>
          }
        />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
